/**
 * Web Audio: буферы, эффекты с разбросом высоты и лимитом голосов, музыка с кроссфейдом, пауза и «без звука».
 * AudioContext создаётся сразу (в suspended), разблокируется `unlock()` по касанию. Музыка качается только после
 * разблокировки: игрок без единого касания её не услышит, а на телефоне это почти весь вес звука.
 */
import {
  DEFAULT_MAX_VOICES,
  pickVariant,
  soundUrl,
  VoiceLimiter,
  type AudioFormat,
  type AudioManifest,
} from "./manifest";

export const CROSSFADE_S = 0.8;
export const FADE_OUT_S = 0.6;
/** Разброс высоты эффектов: ±6 %. */
export const PITCH_SPREAD = 0.06;

export interface AudioEngine {
  /** Разблокировать контекст (вызывать из обработчика жеста). */
  unlock(): void;
  play(id: string, opts?: { rate?: number; gain?: number }): void;
  /** Музыка: id — петля (кроссфейд с текущей), `null` — затихнуть. */
  playMusic(id: string | null): void;
  /** Общая громкость; «без звука» = 0. */
  setOutput(muted: boolean, volume: number): void;
  /** Бой стоит из-за окна: музыка затихает, интерфейсные звуки работают. */
  setBattlePaused(paused: boolean): void;
  /** Приложение свёрнуто: контекст приостанавливается — полная тишина. */
  setHidden(hidden: boolean): void;
  readonly state: AudioContextState | "none";
}

export interface EngineEnv {
  context?: AudioContext;
  fetchBytes?(url: string): Promise<ArrayBuffer>;
  random?(): number;
  format?: AudioFormat;
}

export function createAudioEngine(manifest: AudioManifest, format: AudioFormat, env: EngineEnv = {}): AudioEngine {
  const Ctor: typeof AudioContext | undefined =
    (globalThis as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext ??
    (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  const ctx = env.context ?? (Ctor ? new Ctor() : null);
  if (!ctx) return nullEngine();
  const random = env.random ?? Math.random;
  const fetchBytes = env.fetchBytes ?? (async (url) => (await fetch(url)).arrayBuffer());

  const master = ctx.createGain();
  const sfxBus = ctx.createGain();
  const musicBus = ctx.createGain();
  sfxBus.connect(master);
  musicBus.connect(master);
  master.connect(ctx.destination);

  /** Буферы: `id` -> вариант -> данные. */
  const buffers = new Map<string, Map<number, AudioBuffer>>();
  const loading = new Map<string, Promise<AudioBuffer | null>>();
  const limiter = new VoiceLimiter((id) => manifest.sounds[id]?.voices ?? DEFAULT_MAX_VOICES);
  const lastVariant = new Map<string, number>();
  const lastStart = new Map<string, number>();
  let hidden = false;
  let wantedMusic: string | null = null;
  let music: { id: string; gain: GainNode; source: AudioBufferSourceNode } | null = null;

  const load = (id: string, variant = 0): Promise<AudioBuffer | null> => {
    const have = buffers.get(id)?.get(variant);
    if (have) return Promise.resolve(have);
    const key = `${id}#${variant}`;
    let p = loading.get(key);
    if (!p) {
      p = fetchBytes(soundUrl(manifest, id, format, variant))
        .then(
          (bytes) =>
            new Promise<AudioBuffer>((resolve, reject) => {
              // колбэчная форма — для старых Safari, где decodeAudioData не возвращает промис
              const r = ctx.decodeAudioData(bytes, resolve, reject);
              if (r && typeof r.then === "function") r.then(resolve, reject);
            }),
        )
        .then((b) => {
          let list = buffers.get(id);
          if (!list) buffers.set(id, (list = new Map()));
          list.set(variant, b);
          return b;
        })
        .catch((e: unknown) => {
          loading.delete(key);
          console.warn(`[audio] ${key}: ${String(e)}`);
          return null;
        });
      loading.set(key, p);
    }
    return p;
  };

  for (const [id, s] of Object.entries(manifest.sounds)) {
    if (s.preload) s.variants.forEach((_, i) => void load(id, i));
  }

  const startWanted = (): void => {
    const id = wantedMusic;
    if (id === null || ctx.state !== "running" || music?.id === id) return;
    void load(id).then((buffer) => {
      // пока грузилось, решение могло смениться
      if (buffer && wantedMusic === id && music?.id !== id) startMusic(id, buffer, music ? CROSSFADE_S : 0.3);
    });
  };

  const apply = (): void => {
    if (hidden) void ctx.suspend().catch(() => {});
    else void ctx.resume().then(startWanted, () => {});
  };

  const fadeOut = (m: NonNullable<typeof music>, seconds: number): void => {
    const t = ctx.currentTime;
    m.gain.gain.cancelScheduledValues(t);
    m.gain.gain.setValueAtTime(m.gain.gain.value, t);
    m.gain.gain.linearRampToValueAtTime(0, t + seconds);
    try {
      m.source.stop(t + seconds + 0.05);
    } catch {
      /* уже остановлен */
    }
    m.source.onended = () => m.gain.disconnect();
  };

  const startMusic = (id: string, buffer: AudioBuffer, seconds: number): void => {
    const def = manifest.sounds[id]!;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = def.loop !== false;
    if (def.loopEnd) {
      source.loopStart = def.loopStart ?? 0;
      source.loopEnd = def.loopEnd;
    }
    const gain = ctx.createGain();
    const t = ctx.currentTime;
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(def.volume, t + seconds);
    source.connect(gain).connect(musicBus);
    source.start(0, def.loopStart ?? 0);
    if (music) fadeOut(music, seconds);
    music = { id, gain, source };
  };

  return {
    unlock: apply,
    get state() {
      return ctx.state;
    },
    play(id, opts) {
      const def = manifest.sounds[id];
      if (!def || ctx.state !== "running") return;
      const variant = pickVariant(def.variants.length, lastVariant.get(id), random);
      const buffer = buffers.get(id)?.get(variant);
      const t = ctx.currentTime;
      if (!buffer || t - (lastStart.get(id) ?? -1) < (def.gapMs ?? 0) / 1000 || !limiter.acquire(id)) return;
      lastVariant.set(id, variant);
      lastStart.set(id, t);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.playbackRate.value = (opts?.rate ?? 1) * (1 + (random() * 2 - 1) * PITCH_SPREAD);
      const gain = ctx.createGain();
      gain.gain.value = def.volume * (opts?.gain ?? 1);
      source.connect(gain).connect(sfxBus);
      source.onended = () => {
        limiter.release(id);
        gain.disconnect();
      };
      source.start();
    },
    playMusic(id) {
      wantedMusic = id;
      if (id === null) {
        if (music) fadeOut(music, FADE_OUT_S);
        music = null;
        return;
      }
      startWanted();
    },
    setOutput(muted, volume) {
      master.gain.setTargetAtTime(muted ? 0 : volume, ctx.currentTime, 0.02);
    },
    setBattlePaused(paused) {
      musicBus.gain.setTargetAtTime(paused ? 0 : 1, ctx.currentTime, 0.1);
    },
    setHidden(h) {
      hidden = h;
      apply();
    },
  };
}

function nullEngine(): AudioEngine {
  return {
    unlock() {},
    play() {},
    playMusic() {},
    setOutput() {},
    setBattlePaused() {},
    setHidden() {},
    state: "none",
  };
}
